/**
 * Base class for entity index pages (GraphQL backend)
 *
 * Provides search, browse, and pagination functionality.
 * Subclasses must implement getConfig() to define entity-specific behavior.
 */

import type {
  EntityConfig,
  EntitySearchResult,
  EntitySearchResponse,
  GraphQLResponse,
  SearchEntitiesData,
  BrowseEntitiesData,
  SortOrder,
  FilterTab
} from './types';
import type { EntityFilterInput, FacetBucket } from './types';
import { belowHeaderInsertPoint } from './page-header';
import { FILTER_BAR_STYLES, FilterBar } from './filter-bar';
import { premiumRequiredHtml } from './leaf-detail';
import { currentCycle } from './cycle-facet';
import { EntityTypeahead, suggestTypes } from './entity-suggest';
import { BROWSE_ENTITIES_QUERY } from './browse-query';

// Constants
const PAGE_SIZE = 50;
const GRAPHQL_ENDPOINT = '/api/graphql';
const CYCLE_ENTITY_TYPES = new Set(['Politician', 'Committee', 'Party', 'Office', 'District', 'State', 'Cycle', 'Individual', 'Vendor', 'Voter']);

// Fixed documents (persisted-query list, sites#277). The optional arguments are
// always declared; a variable the request leaves out is the same as no argument.
// pageSize is written in (50 = PAGE_SIZE), so a visitor cannot raise it.
const SEARCH_ENTITIES_QUERY = `
query SearchEntities($query: String!, $entityType: EntityType!, $page: Int!, $sortOrder: SortOrder!, $filter: String, $filters: [EntityFilterInput!]) {
  searchEntities(query: $query, entityType: $entityType, page: $page, pageSize: 50, sortOrder: $sortOrder, filter: $filter, filters: $filters) {
    results {
      id
      type
      name
      score
      state
      city
      party
      office
      district
      metadata
      affinityDem
      affinityRep
    }
    total
    page
    pageSize
    hasMore
  }
}
`;


// Cycle-only fixed documents: the original documents above remain byte-identical.
const CYCLE_SEARCH_ENTITIES_QUERY = `
query CycleSearchEntities($cycle: Int!, $query: String!, $entityType: EntityType!, $page: Int!, $sortOrder: SortOrder!, $filter: String, $filters: [EntityFilterInput!]) {
  searchEntities(cycle: $cycle, query: $query, entityType: $entityType, page: $page, pageSize: 50, sortOrder: $sortOrder, filter: $filter, filters: $filters) {
    results {
      id
      type
      name
      score
      state
      city
      party
      office
      district
      metadata
      affinityDem
      affinityRep
    }
    total
    page
    pageSize
    hasMore
  }
}
`;

const CYCLE_BROWSE_ENTITIES_QUERY = `
query CycleBrowseEntities($cycle: Int!, $entityType: EntityType!, $page: Int!, $sortOrder: SortOrder!, $sortField: String, $filter: String, $filters: [EntityFilterInput!]) {
  browseEntities(cycle: $cycle, entityType: $entityType, page: $page, pageSize: 50, sortOrder: $sortOrder, sortField: $sortField, filter: $filter, filters: $filters) {
    results {
      id
      type
      name
      score
      state
      city
      party
      office
      district
      metadata
      affinityDem
      affinityRep
    }
    total
    page
    pageSize
    hasMore
  }
}
`;

function escapeHtml(str: string | undefined | null): string {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export abstract class EntityIndex {
  private loadGeneration = 0;
  protected currentPage = 1;
  protected currentQuery = '';
  protected currentFilter: string | null = null;
  protected hasMore = false;
  protected isSearchMode = false;
  protected typeahead: EntityTypeahead | null = null;
  /** Structured filters (config.filterControls; filter-bar.ts). */
  protected filterBar: FilterBar | null = null;
  protected indexSelector = '.entity-list, .alphabetical-index, section > ul';

  abstract getConfig(): EntityConfig;

  constructor() {
    this.init();
  }

  protected init(): void {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  protected setup(): void {
    this.injectStyles();
    this.insertSearchUI();
    this.showCycleNotice();
    this.attachEventListeners();
    this.loadFromURL();
  }

  /** A cycle is sent only where the backend has an explicit membership contract. */
  protected selectedCycle(): number | null {
    return CYCLE_ENTITY_TYPES.has(this.getConfig().entityType) && !this.cycleUnavailable ? currentCycle() : null;
  }

  /** Set when the API answers CYCLE_UNAVAILABLE: the index has no cycle history yet (voters before rundeck#846). */
  protected cycleUnavailable = false;

  protected showCycleNotice(): void {
    const cycle = currentCycle();
    const previous = document.querySelector<HTMLElement>('.ei-cycle-notice');
    if (!cycle) { previous?.remove(); return; }
    const notice = previous ?? document.createElement('p');
    notice.className = 'ei-cycle-notice';
    notice.dataset.cycle = String(cycle);
    const entityType = this.getConfig().entityType;
    notice.textContent = this.selectedCycle() !== null
      ? entityType === 'Voter'
        ? `Results and counts show registered voters who voted in the ${cycle - 1}–${cycle} cycle (Civitech vote history from 2012, for people registered now).`
        : `Results and counts show entities active in the ${cycle - 1}–${cycle} cycle. Financial and profile figures cover all cycles.` +
          (entityType === 'Individual' ? ' The search index snapshot may lag homepage graph counts.' : '')
      : this.cycleUnavailable
        ? 'Results and counts cover all cycles; voter cycle history is still being indexed.'
      : entityType === 'Employer'
        ? 'Results and counts cover all cycles. Cycle-filtered employer lists are temporarily unavailable because they require slow graph scans.'
        : 'Results and counts cover all cycles; election-cycle membership is not available for this entity type.';
    if (!previous) document.querySelector('.entity-search-container')?.before(notice);
  }

  protected injectStyles(): void {
    const style = document.createElement('style');
    style.textContent = `
      .entity-search-container {
        background: white;
        padding: 20px;
        border-radius: 8px;
        margin-bottom: 20px;
        border: 1px solid var(--border, #e2e8f0);
      }
      .entity-search-form {
        display: flex;
        gap: 10px;
      }
      .entity-search-form input {
        flex: 1;
        padding: 12px 16px;
        font-size: 1rem;
        border: 2px solid var(--border, #e2e8f0);
        border-radius: 6px;
        font-family: inherit;
      }
      .entity-search-form input:focus {
        outline: none;
        border-color: var(--secondary, #2c5282);
      }
      .entity-search-form button {
        padding: 12px 24px;
        font-size: 1rem;
        background: var(--secondary, #2c5282);
        color: white;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        font-family: inherit;
      }
      .entity-search-form button:hover {
        background: var(--primary, #1a365d);
      }
      .entity-search-results {
        margin-top: 15px;
      }
      .entity-search-results-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 15px;
        padding-bottom: 10px;
        border-bottom: 1px solid var(--border, #e2e8f0);
      }
      .entity-search-results-header h3 {
        margin: 0;
        color: var(--primary, #1a365d);
      }
      .entity-search-clear {
        background: none;
        border: 1px solid var(--border, #e2e8f0);
        padding: 6px 12px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.9em;
      }
      .entity-search-clear:hover {
        background: var(--bg, #f7fafc);
      }
      .entity-search-result {
        display: block;
        padding: 12px 16px;
        border: 1px solid var(--border, #e2e8f0);
        border-radius: 6px;
        margin-bottom: 8px;
        text-decoration: none;
        color: inherit;
      }
      .entity-search-result:hover {
        background: var(--bg, #f7fafc);
        border-color: var(--secondary, #2c5282);
      }
      .entity-search-result-name {
        font-weight: 600;
        color: var(--primary, #1a365d);
      }
      .entity-search-result-meta {
        font-size: 0.9em;
        color: #718096;
        margin-top: 2px;
      }
      .entity-results-count {
        font-size: 0.9em;
        color: #718096;
        margin-left: auto;
        margin-right: 12px;
      }
      .entity-search-result-affinity {
        display: block;
        font-size: 0.85em;
        margin-top: 4px;
      }
      .entity-search-message {
        padding: 20px;
        text-align: center;
        color: #718096;
      }
      .entity-search-error {
        color: #c53030;
        background: #fff5f5;
        border-radius: 6px;
      }
      .entity-index-hidden {
        display: none !important;
      }
      .entity-pagination {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 8px;
        margin-top: 20px;
        padding-top: 20px;
        border-top: 1px solid var(--border, #e2e8f0);
        flex-wrap: wrap;
      }
      .entity-pagination-info {
        width: 100%;
        text-align: center;
        color: #718096;
        font-size: 0.9em;
        margin-bottom: 10px;
      }
      .entity-pagination button {
        padding: 8px 12px;
        border: 1px solid var(--border, #e2e8f0);
        background: white;
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.9em;
        min-width: 40px;
      }
      .entity-pagination button:hover:not(:disabled) {
        background: var(--bg, #f7fafc);
        border-color: var(--secondary, #2c5282);
      }
      .entity-pagination button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      .entity-pagination button.active {
        background: var(--secondary, #2c5282);
        color: white;
        border-color: var(--secondary, #2c5282);
      }
      .entity-loading {
        text-align: center;
        padding: 40px;
        color: #718096;
      }
      .entity-loading::after {
        content: '';
        display: inline-block;
        width: 20px;
        height: 20px;
        border: 2px solid #e2e8f0;
        border-top-color: var(--secondary, #2c5282);
        border-radius: 50%;
        animation: entity-spin 0.8s linear infinite;
        margin-left: 10px;
        vertical-align: middle;
      }
      @keyframes entity-spin {
        to { transform: rotate(360deg); }
      }
      .entity-filter-tabs {
        display: flex;
        gap: 8px;
        margin-top: 15px;
        padding-bottom: 15px;
        border-bottom: 1px solid var(--border, #e2e8f0);
        flex-wrap: wrap;
      }
      .entity-filter-tab {
        padding: 8px 16px;
        border: 1px solid var(--border, #e2e8f0);
        border-radius: 20px;
        background: white;
        cursor: pointer;
        font-size: 0.9em;
        font-family: inherit;
        color: var(--text, #2d3748);
        transition: all 0.15s ease;
      }
      .entity-filter-tab:hover {
        background: var(--bg, #f7fafc);
        border-color: var(--secondary, #2c5282);
      }
      .entity-filter-tab.active {
        background: var(--secondary, #2c5282);
        color: white;
        border-color: var(--secondary, #2c5282);
      }
      .entity-filter-dropdown {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-top: 15px;
        padding-bottom: 15px;
        border-bottom: 1px solid var(--border, #e2e8f0);
      }
      .entity-filter-dropdown label {
        font-weight: 500;
        color: var(--text, #2d3748);
      }
      .entity-filter-dropdown select {
        padding: 8px 12px;
        font-size: 0.95rem;
        border: 1px solid var(--border, #e2e8f0);
        border-radius: 6px;
        background: white;
        cursor: pointer;
        font-family: inherit;
        min-width: 200px;
      }
      .entity-filter-dropdown select:focus {
        outline: none;
        border-color: var(--secondary, #2c5282);
      }
    ` + (this.getConfig().filterControls ? FILTER_BAR_STYLES : '');
    document.head.appendChild(style);
  }

  protected insertSearchUI(): void {
    const config = this.getConfig();
    const container = document.createElement('div');
    container.className = 'entity-search-container';

    // Build tabs HTML if configured
    let tabsHtml = '';
    if (config.tabs && config.tabs.length > 0) {
      const tabButtons = config.tabs.map((tab, index) =>
        `<button type="button" class="entity-filter-tab${index === 0 ? ' active' : ''}" data-filter="${tab.filter || ''}" data-tab-id="${tab.id}">${tab.label}</button>`
      ).join('');
      tabsHtml = `<div class="entity-filter-tabs" id="entity-filter-tabs">${tabButtons}</div>`;
    }

    // Build dropdown HTML if configured
    let dropdownHtml = '';
    if (config.filterDropdown) {
      const options = config.filterDropdown.options.map(opt =>
        `<option value="${opt.value}">${opt.label}</option>`
      ).join('');
      dropdownHtml = `
        <div class="entity-filter-dropdown" id="entity-filter-dropdown">
          <label for="entity-filter-select">${config.filterDropdown.label}:</label>
          <select id="entity-filter-select">
            <option value="">All</option>
            ${options}
          </select>
        </div>
      `;
    }

    container.innerHTML = `
      <form class="entity-search-form" id="entity-search-form">
        <input type="text" id="entity-search-input" placeholder="${config.placeholder}" autocomplete="off">
        <button type="submit">Search</button>
      </form>
      ${tabsHtml}
      ${dropdownHtml}
      ${config.filterControls ? '<div id="entity-filter-bar"></div>' : ''}
      <div class="entity-search-results" id="entity-search-results">
        <div class="entity-search-results-header">
          <h3 id="entity-results-title">${config.browseTitle}</h3>
          ${config.filterControls || this.selectedCycle() !== null ? '<span class="entity-results-count" id="entity-results-count"></span>' : ''}
          <button type="button" class="entity-search-clear" id="entity-search-clear" style="display: none;">Show All</button>
        </div>
        <div id="entity-search-results-list"></div>
        <div id="entity-pagination"></div>
      </div>
    `;

    const insertPoint = belowHeaderInsertPoint();
    if (insertPoint?.parentNode) {
      insertPoint.parentNode.insertBefore(container, insertPoint);
    }

    const filterMount = container.querySelector<HTMLElement>('#entity-filter-bar');
    if (config.filterControls && filterMount) {
      this.filterBar = new FilterBar(
        filterMount,
        config.filterControls,
        (field, filters, size) => this.loadFacets(field, filters, size),
        () => this.loadEntities({ query: this.currentQuery, page: 1 }),
        () => this.updateResultsTitle()
      );
    }
  }

  /** Results heading: browse title or search query, plus the active filters. */
  protected updateResultsTitle(): void {
    const resultsTitle = document.getElementById('entity-results-title');
    if (!resultsTitle) return;
    const filterSummary = this.filterBar?.describe() ?? '';
    const base = this.isSearchMode ? `Search Results for "${this.currentQuery}"` : this.getConfig().browseTitle;
    resultsTitle.textContent = filterSummary ? `${base} · ${filterSummary}` : base;
  }

  /** Facet values with counts for a filter dropdown (entityFacets). */
  protected async loadFacets(field: string, filters: EntityFilterInput[], size: number): Promise<FacetBucket[]> {
    const config = this.getConfig();
    const cycle = this.selectedCycle();
    const res = await fetch(config.graphqlEndpoint || GRAPHQL_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: cycle !== null
          ? `query CycleEntityFacets($cycle: Int!, $entityType: EntityType!, $field: String!, $filters: [EntityFilterInput!], $size: Int) {
          entityFacets(cycle: $cycle, entityType: $entityType, field: $field, filters: $filters, size: $size) { value label count }
        }`
          : `query EntityFacets($entityType: EntityType!, $field: String!, $filters: [EntityFilterInput!], $size: Int) {
          entityFacets(entityType: $entityType, field: $field, filters: $filters, size: $size) { value label count }
        }`,
        variables: { entityType: config.entityType, field, filters, size, ...(cycle !== null ? { cycle } : {}) }
      })
    });
    if (!res.ok) throw new Error(`Facets failed (${res.status})`);
    const json = await res.json() as GraphQLResponse<{ entityFacets: FacetBucket[] }>;
    if (json.errors?.length) throw new Error(json.errors[0].message);
    return json.data?.entityFacets ?? [];
  }

  protected attachEventListeners(): void {
    const input = document.getElementById('entity-search-input') as HTMLInputElement | null;
    const config = this.getConfig();
    const types = suggestTypes(config.entityType);
    if (input && types) {
      this.typeahead = new EntityTypeahead(input, {
        types, cycle: () => this.selectedCycle(), endpoint: config.graphqlEndpoint
      });
    }
    document.getElementById('entity-search-form')?.addEventListener('submit', (e) => {
      e.preventDefault();
      const input = document.getElementById('entity-search-input') as HTMLInputElement;
      const query = input?.value.trim() || '';
      this.loadEntities({ query, page: 1 });
    });

    document.getElementById('entity-search-clear')?.addEventListener('click', () => {
      this.clearSearch();
    });

    // Tab click handlers
    document.getElementById('entity-filter-tabs')?.querySelectorAll('.entity-filter-tab').forEach(tab => {
      tab.addEventListener('click', () => {
        const filterValue = (tab as HTMLButtonElement).dataset.filter || null;
        this.currentFilter = filterValue || null;

        // Update active state
        document.querySelectorAll('.entity-filter-tab').forEach(t => t.classList.remove('active'));
        tab.classList.add('active');

        // Reload with new filter
        this.loadEntities({ query: this.currentQuery, page: 1 });
      });
    });

    // Dropdown change handler
    document.getElementById('entity-filter-select')?.addEventListener('change', (e) => {
      const select = e.target as HTMLSelectElement;
      this.currentFilter = select.value || null;
      this.loadEntities({ query: this.currentQuery, page: 1 });
    });

    window.addEventListener('popstate', (e) => this.handlePopState(e));
  }

  protected loadFromURL(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('q') || '';
    const page = parseInt(urlParams.get('page') || '1', 10) || 1;

    if (query) {
      const input = document.getElementById('entity-search-input') as HTMLInputElement;
      if (input) input.value = query;
    }

    if (this.filterBar) {
      this.filterBar.readURL(urlParams);
      this.filterBar.mount();
    }

    this.loadEntities({ query, page, updateHistory: false });
  }

  /**
   * Get the effective filter by merging fixedFilter with any UI-selected filter.
   */
  protected getEffectiveFilter(): string | null {
    const config = this.getConfig();
    const parts: string[] = [];
    if (config.fixedFilter) parts.push(config.fixedFilter);
    if (this.currentFilter) parts.push(this.currentFilter);
    return parts.length > 0 ? parts.join(',') : null;
  }

  protected async loadEntities(options: { query?: string; page?: number; updateHistory?: boolean } = {}): Promise<void> {
    const generation = ++this.loadGeneration;
    const config = this.getConfig();
    const page = options.page || 1;
    const query = options.query || '';
    const updateHistory = options.updateHistory !== false;
    this.typeahead?.close();

    const resultsList = document.getElementById('entity-search-results-list');
    const paginationContainer = document.getElementById('entity-pagination');
    const clearButton = document.getElementById('entity-search-clear');
    const indexElements = document.querySelectorAll(this.indexSelector);

    this.currentPage = page;
    this.currentQuery = query;
    this.isSearchMode = !!query;

    indexElements.forEach(el => el.classList.add('entity-index-hidden'));

    if (resultsList) resultsList.innerHTML = '<div class="entity-loading">Loading</div>';
    if (paginationContainer) paginationContainer.innerHTML = '';

    this.updateResultsTitle();
    if (clearButton) clearButton.style.display = this.isSearchMode ? 'block' : 'none';
    const resultsCount = document.getElementById('entity-results-count');
    if (resultsCount) resultsCount.textContent = '';

    if (updateHistory) {
      this.updateURL(query, page);
    }

    // Build GraphQL query
    const effectiveFilter = this.getEffectiveFilter();
    const graphqlQuery = this.isSearchMode
      ? this.buildSearchQuery()
      : this.buildBrowseQuery();

    const cycle = this.selectedCycle();
    const variables: Record<string, unknown> = {
      ...(cycle !== null ? { cycle } : {}),
      entityType: config.entityType,
      page,
      sortOrder: config.sortOrder,
      ...(this.isSearchMode && { query }),
      ...(config.sortField && { sortField: config.sortField }),
      ...(effectiveFilter && { filter: effectiveFilter }),
      ...(this.hasStructuredFilters() && { filters: this.filterBar!.toInputs() })
    };

    try {
      const res = await fetch(config.graphqlEndpoint || GRAPHQL_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: graphqlQuery, variables })
      });

      if (generation !== this.loadGeneration) return;
      if (!res.ok) {
        if (resultsList) resultsList.innerHTML = `<p class="entity-search-message entity-search-error">Failed to load: ${res.status}</p>`;
        return;
      }

      const json: GraphQLResponse<SearchEntitiesData | BrowseEntitiesData> = await res.json();
      if (generation !== this.loadGeneration) return;

      if (json.errors) {
        // No cycle history in the index yet: once, fall back to all cycles (a
        // voting-history filter still needs it, so a second refusal is shown).
        if (!this.cycleUnavailable && json.errors.some(e => e.extensions?.code === 'CYCLE_UNAVAILABLE')) {
          this.cycleUnavailable = true;
          this.showCycleNotice();
          if (cycle !== null) return this.loadEntities({ ...options, updateHistory: false });
        }
        if (resultsList) {
          resultsList.innerHTML = json.errors.some(e => e.extensions?.code === 'FORBIDDEN')
            // Premium data (voters, individual donors; sites#277)
            ? `<div class="entity-search-message">${premiumRequiredHtml('These records')}</div>`
            : json.errors.some(e => e.extensions?.code === 'CYCLE_UNAVAILABLE')
            ? '<p class="entity-search-message">Voting history by cycle is still being indexed. Clear the voting-history filters to see all voters.</p>'
            : `<p class="entity-search-message entity-search-error">Error: ${escapeHtml(json.errors[0]?.message || 'Unknown error')}</p>`;
        }
        return;
      }

      const response = this.isSearchMode
        ? (json.data as SearchEntitiesData)?.searchEntities
        : (json.data as BrowseEntitiesData)?.browseEntities;

      if (!response) {
        if (resultsList) resultsList.innerHTML = '<p class="entity-search-message entity-search-error">No response data</p>';
        return;
      }

      this.hasMore = response.hasMore;
      if (resultsCount) resultsCount.textContent = `${response.total.toLocaleString()} ${response.total === 1 ? 'match' : 'matches'}`;
      this.displayResults(response.results, config);
      this.displayPagination(page, response.results.length);

    } catch (err) {
      if (generation !== this.loadGeneration) return;
      if (resultsList) resultsList.innerHTML = `<p class="entity-search-message entity-search-error">Error: ${(err as Error).message}</p>`;
    }
  }

  protected hasStructuredFilters(): boolean {
    return !!this.filterBar?.hasFilters();
  }

  protected buildSearchQuery(): string {
    return this.selectedCycle() !== null ? CYCLE_SEARCH_ENTITIES_QUERY : SEARCH_ENTITIES_QUERY;
  }

  protected buildBrowseQuery(): string {
    return this.selectedCycle() !== null ? CYCLE_BROWSE_ENTITIES_QUERY : BROWSE_ENTITIES_QUERY;
  }

  protected displayResults(results: EntitySearchResult[], config: EntityConfig): void {
    const resultsList = document.getElementById('entity-search-results-list');
    if (!resultsList) return;

    if (!results.length) {
      if (this.isSearchMode) {
        resultsList.innerHTML = '<p class="entity-search-message">No results found. Try a different search term.</p>';
      } else {
        resultsList.innerHTML = '<p class="entity-search-message">No entities found.</p>';
      }
      return;
    }

    const html = results.map(result => {
      const formatted = config.formatResult(result);
      const dem = result.affinityDem || 0;
      const rep = result.affinityRep || 0;
      const affinityAttrs = (dem || rep)
        ? ` data-affinity-dem="${dem.toFixed(3)}" data-affinity-rep="${rep.toFixed(3)}"`
        : '';
      const affinityLabel = (dem || rep)
        ? `<span class="entity-search-result-affinity"><span>Party Affinity – </span><span style="color:#2563eb">DEM: ${Math.round(dem * 100)}%</span> <span style="color:#dc2626">REP: ${Math.round(rep * 100)}%</span></span>`
        : '';
      return `
        <a href="${formatted.url}" class="entity-search-result"${affinityAttrs}>
          <div class="entity-search-result-name">${escapeHtml(formatted.name)}</div>
          <div class="entity-search-result-meta">${escapeHtml(formatted.meta)}</div>
          ${affinityLabel}
        </a>
      `;
    }).join('');

    resultsList.innerHTML = html;
    this.applyAffinityTinting(resultsList);
  }

  /**
   * Apply party affinity background tinting to elements with data-affinity-dem/rep attributes.
   * Same logic as affinity.js but runs inline after dynamic result rendering.
   */
  protected applyAffinityTinting(container: HTMLElement): void {
    container.querySelectorAll<HTMLElement>('[data-affinity-dem]').forEach(el => {
      const dem = parseFloat(el.dataset.affinityDem || '0');
      const rep = parseFloat(el.dataset.affinityRep || '0');
      if (dem === 0 && rep === 0) return;

      const diff = dem - rep;
      const intensity = Math.abs(diff) * 0.45;

      if (diff > 0.05) {
        el.style.backgroundColor = `rgba(59, 130, 246, ${intensity})`; // Blue (DEM)
      } else if (diff < -0.05) {
        el.style.backgroundColor = `rgba(220, 38, 38, ${intensity})`; // Red (REP)
      }

      // Dark text on every tint: at most 45% alpha over white, the tint stays
      // light, and white text on it (the old rule above 0.20) was unreadable.
      if (Math.abs(diff) > 0.05) {
        el.querySelectorAll<HTMLElement>('div').forEach(child => {
          child.style.color = '#000';
        });
      }
    });
  }

  protected displayPagination(page: number, resultCount: number): void {
    const paginationContainer = document.getElementById('entity-pagination');
    if (!paginationContainer) return;

    // No pagination needed if first page with fewer than a full page of results
    if (page === 1 && !this.hasMore) {
      paginationContainer.innerHTML = resultCount > 0
        ? `<div class="entity-pagination-info">Showing all ${resultCount.toLocaleString()} results</div>`
        : '';
      return;
    }

    const start = (page - 1) * PAGE_SIZE + 1;
    const end = start + resultCount - 1;

    let html = `<div class="entity-pagination-info">Showing ${start.toLocaleString()}-${end.toLocaleString()}</div>`;
    html += '<div class="entity-pagination">';
    html += `<button type="button" data-page="1" ${page === 1 ? 'disabled' : ''}>First</button>`;
    html += `<button type="button" data-page="${page - 1}" ${page === 1 ? 'disabled' : ''}>Prev</button>`;
    html += `<button type="button" class="active">${page}</button>`;
    html += `<button type="button" data-page="${page + 1}" ${!this.hasMore ? 'disabled' : ''}>Next</button>`;
    html += '</div>';

    paginationContainer.innerHTML = html;

    paginationContainer.querySelectorAll('button[data-page]').forEach(btn => {
      btn.addEventListener('click', () => {
        const targetPage = parseInt((btn as HTMLButtonElement).dataset.page || '1', 10);
        if (targetPage >= 1 && targetPage !== page) {
          this.loadEntities({ page: targetPage, query: this.currentQuery });
          document.getElementById('entity-search-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  protected updateURL(query: string, page: number): void {
    const url = new URL(window.location.href);

    if (query) {
      url.searchParams.set('q', query);
    } else {
      url.searchParams.delete('q');
    }

    if (page > 1) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.delete('page');
    }

    this.filterBar?.writeURL(url);

    window.history.pushState({ query, page }, '', url.toString());
  }

  protected handlePopState(event: PopStateEvent): void {
    this.showCycleNotice();
    const state = event.state || {};
    const urlParams = new URLSearchParams(window.location.search);

    const query = state.query ?? urlParams.get('q') ?? '';
    const page = state.page ?? (parseInt(urlParams.get('page') || '1', 10) || 1);

    const input = document.getElementById('entity-search-input') as HTMLInputElement;
    if (input) input.value = query;

    this.filterBar?.syncFromURL(urlParams);

    this.loadEntities({ query, page, updateHistory: false });
  }

  protected clearSearch(): void {
    const input = document.getElementById('entity-search-input') as HTMLInputElement;
    if (input) input.value = '';
    this.loadEntities({ query: '', page: 1 });
  }
}
